// AppLogo — square logo container with image fallback.
// Mirrors `.applogo` in /Users/harry/Downloads/employee-portal/project/styles.css
// (lines 347–361) and AppLogo()/AppLogoSquare() in components.jsx (lines 61–145).
//
// When a `logoUrl` is supplied, the container switches to white background with a
// 1px neutral.200 border, and the image is inset by ~18% padding so it doesn't
// touch the edges. Without a URL, the abbreviation (first two letters of `name`)
// is rendered on a `bg`-tinted square.

import { Box, Image } from "@chakra-ui/react";
import { forwardRef, useState } from "react";

export type AppLogoSize = "xs" | "sm" | "md" | "lg" | "xl";

export interface AppLogoProps {
  /** App / resource name. Used to derive the 2-letter fallback abbreviation. */
  name: string;
  /** Optional brand logo URL (e.g. simpleicons CDN). When set, switches to image mode. */
  logoUrl?: string;
  /** Optional fallback background color when no logoUrl is supplied. */
  bg?: string;
  /** Logo size. Default `md` (32×32). */
  size?: AppLogoSize;
}

// Per-size dimensions, font sizes and border radii. The CSS `.applogo` base
// declares `border-radius: 7px`, but `AppLogo()` in components.jsx inlines
// `borderRadius: "8px"` + `fontSize: "10px"` over all sizes, so the rendered
// truth is 8px radius / 10px font. We match the rendered values.
// xs:  16×16, font 8px,  radius 4px (theme `component.sm`)
// sm:  20×20, font 9px,  radius 5px (inline literal — no token)
// md:  32×32, font 10px, radius 8px (theme `component.lg`)
// lg:  40×40, font 13px, radius 8px (theme `component.lg`)
// xl:  48×48, font 15px, radius 8px (theme `component.lg`)
const sizeMap: Record<
  AppLogoSize,
  { px: number; fontSize: string; radius: string }
> = {
  xs: { px: 16, fontSize: "8px", radius: "component.sm" }, // 4px
  sm: { px: 20, fontSize: "9px", radius: "5px" }, // no token at 5px
  md: { px: 32, fontSize: "10px", radius: "component.lg" }, // 8px
  lg: { px: 40, fontSize: "13px", radius: "component.lg" }, // 8px
  xl: { px: 48, fontSize: "15px", radius: "component.lg" }, // 8px
};

function abbr(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  // First two letters of the name, uppercased.
  return trimmed.slice(0, 2).toUpperCase();
}

const AppLogo = forwardRef<HTMLSpanElement, AppLogoProps>(
  ({ name, logoUrl, bg, size = "md" }, ref) => {
    const { px, fontSize, radius } = sizeMap[size];
    const pad = Math.round(px * 0.18);
    const inner = px - pad * 2;

    // Track broken / unloadable image URLs so we can fall back to the
    // abbreviation tile. Some backend rows have an `imageUrl` that 404s or
    // points at a generic Ploy placeholder; we want the same chrome as the
    // "no logoUrl" branch in those cases.
    const [imgFailed, setImgFailed] = useState(false);
    const hasImage = Boolean(logoUrl) && !imgFailed;

    if (hasImage) {
      return (
        <Box
          as="span"
          ref={ref}
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          overflow="hidden"
          width={`${px}px`}
          height={`${px}px`}
          borderRadius={radius}
          bg="white"
          borderWidth="1px"
          borderStyle="solid"
          // Pure-black outline at 8% alpha — image outlines must be neutral
          // (rgba) not tinted, otherwise the border picks up the surface
          // colour underneath and reads as "dirt" on the edge.
          borderColor="rgba(0, 0, 0, 0.08)"
          color="white"
          fontFamily="inherit"
          fontWeight={700}
          fontSize={fontSize}
        >
          <Image
            src={logoUrl}
            alt={name}
            width={`${inner}px`}
            height={`${inner}px`}
            display="block"
            onError={() => setImgFailed(true)}
          />
        </Box>
      );
    }

    return (
      <Box
        as="span"
        ref={ref}
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        overflow="hidden"
        width={`${px}px`}
        height={`${px}px`}
        borderRadius={radius}
        bg={bg || "neutral.200"}
        color="white"
        fontFamily="inherit"
        fontWeight={700}
        fontSize={fontSize}
      >
        {abbr(name)}
      </Box>
    );
  },
);

AppLogo.displayName = "AppLogo";

export default AppLogo;
